import React from 'react';
import LegalPage from './LegalPage';

export default function GDPR() {
  return (
    <LegalPage title="GDPR Compliance" updated="April 15, 2026">

      <p>ClickSaver is committed to compliance with the General Data Protection Regulation (GDPR) (EU) 2016/679. This page outlines our data protection practices, your rights as a data subject, and how we handle personal data in accordance with GDPR requirements.</p>

      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '16px 20px', marginBottom: 32 }}>
        <strong style={{ color: '#1d4ed8', fontSize: 14 }}>For EU/EEA residents:</strong>
        <p style={{ color: '#1e40af', fontSize: 14, marginTop: 4, marginBottom: 0 }}>If you are located in the European Union or European Economic Area, GDPR grants you specific rights regarding your personal data. This page explains those rights and how to exercise them.</p>
      </div>

      <div className="legal-toc">
        <strong style={{ fontSize: 14, color: '#0f172a' }}>Table of Contents</strong>
        <ul style={{ marginTop: 10 }}>
          <li><a href="#controller">1. Data Controller</a></li>
          <li><a href="#lawful">2. Lawful Basis for Processing</a></li>
          <li><a href="#data">3. Personal Data We Process</a></li>
          <li><a href="#rights">4. Your GDPR Rights</a></li>
          <li><a href="#transfers">5. International Data Transfers</a></li>
          <li><a href="#retention">6. Data Retention</a></li>
          <li><a href="#security">7. Security Measures</a></li>
          <li><a href="#processors">8. Data Processors</a></li>
          <li><a href="#dpo">9. Data Protection Officer</a></li>
          <li><a href="#complaint">10. Right to Lodge a Complaint</a></li>
          <li><a href="#contact">11. Contact</a></li>
        </ul>
      </div>

      <h2 id="controller">1. Data Controller</h2>
      <p>ClickSaver acts as the <strong>data controller</strong> for personal data collected from our customers and website visitors. As the data controller, we determine the purposes and means of processing your personal data.</p>
      <p>When you install the ClickSaver tracking script on your website, you become the data controller for your website visitors' data, and ClickSaver acts as a <strong>data processor</strong> on your behalf.</p>

      <div className="legal-divider" />

      <h2 id="lawful">2. Lawful Basis for Processing</h2>
      <p>We process personal data based on the following lawful bases under GDPR Article 6:</p>
      <ul>
        <li><strong>Contractual necessity (Art. 6(1)(b)):</strong> Processing your account information and payment data to provide the service you subscribed to.</li>
        <li><strong>Legitimate interests (Art. 6(1)(f)):</strong> Processing click data and IP addresses to detect fraud and protect our customers' advertising budgets — balanced against data subjects' interests and rights.</li>
        <li><strong>Legal obligation (Art. 6(1)(c)):</strong> Retaining transaction records as required by applicable tax and financial laws.</li>
        <li><strong>Consent (Art. 6(1)(a)):</strong> For marketing communications, where applicable.</li>
      </ul>

      <div className="legal-divider" />

      <h2 id="data">3. Personal Data We Process</h2>

      <h3>Customer Account Data</h3>
      <ul>
        <li>Name and email address</li>
        <li>Billing name and address</li>
        <li>Payment method details (tokenized, managed by Stripe)</li>
        <li>Subscription history</li>
      </ul>

      <h3>Click Tracking Data (Website Visitors)</h3>
      <ul>
        <li>IP addresses (pseudonymized after 30 days)</li>
        <li>Browser user agent strings</li>
        <li>Referring URLs and click source parameters</li>
        <li>Timestamps of click events</li>
      </ul>
      <p>We apply the principle of <strong>data minimization</strong> — we only collect what is necessary for fraud detection purposes.</p>

      <div className="legal-divider" />

      <h2 id="rights">4. Your GDPR Rights</h2>
      <p>Under the GDPR, you have the following rights:</p>

      <h3>Right of Access (Art. 15)</h3>
      <p>You have the right to obtain confirmation of whether we process your personal data and, if so, to receive a copy of that data.</p>

      <h3>Right to Rectification (Art. 16)</h3>
      <p>You have the right to have inaccurate personal data corrected and incomplete data completed.</p>

      <h3>Right to Erasure / Right to be Forgotten (Art. 17)</h3>
      <p>You have the right to request deletion of your personal data where it is no longer necessary for the purpose it was collected, or if you withdraw consent.</p>

      <h3>Right to Restriction of Processing (Art. 18)</h3>
      <p>You have the right to request that we restrict the processing of your personal data under certain circumstances.</p>

      <h3>Right to Data Portability (Art. 20)</h3>
      <p>You have the right to receive your personal data in a structured, commonly used, and machine-readable format.</p>

      <h3>Right to Object (Art. 21)</h3>
      <p>You have the right to object to processing based on legitimate interests. We will cease processing unless we demonstrate compelling legitimate grounds that override your interests.</p>

      <h3>Rights Related to Automated Decision Making (Art. 22)</h3>
      <p>Our fraud scoring system involves automated processing. You have the right to request human review of automated decisions that significantly affect you. Contact us to request a manual review of any IP block or fraud classification.</p>

      <p style={{ marginTop: 16 }}>To exercise any of these rights, please email us at <a href="mailto:support@clicksaver.app">support@clicksaver.app</a>. We will respond within 30 days.</p>

      <div className="legal-divider" />

      <h2 id="transfers">5. International Data Transfers</h2>
      <p>ClickSaver may transfer personal data outside the European Economic Area (EEA). Where we do, we ensure appropriate safeguards are in place, including:</p>
      <ul>
        <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
        <li>Transfers only to countries with an adequacy decision from the European Commission</li>
        <li>Binding Corporate Rules where applicable</li>
      </ul>
      <p>Our infrastructure providers (Google Firebase, Render, Stripe) maintain their own GDPR compliance programs.</p>

      <div className="legal-divider" />

      <h2 id="retention">6. Data Retention</h2>
      <ul>
        <li><strong>Account data:</strong> Retained for the duration of your subscription plus 3 years for legal compliance</li>
        <li><strong>Click and IP data:</strong> Retained for 12 months for fraud analysis, then deleted or anonymized</li>
        <li><strong>IP addresses:</strong> Pseudonymized after 30 days (last octet removed)</li>
        <li><strong>Payment records:</strong> Retained for 7 years as required by tax law</li>
        <li><strong>Support communications:</strong> Retained for 2 years</li>
      </ul>

      <div className="legal-divider" />

      <h2 id="security">7. Security Measures</h2>
      <p>We implement the following technical and organizational security measures:</p>
      <ul>
        <li>End-to-end encryption (TLS 1.3) for all data in transit</li>
        <li>AES-256 encryption for data at rest</li>
        <li>Role-based access controls — only authorized personnel access personal data</li>
        <li>Regular security audits and penetration testing</li>
        <li>Incident response plan with 72-hour breach notification capability</li>
        <li>Employee data protection training</li>
      </ul>

      <div className="legal-divider" />

      <h2 id="processors">8. Data Processors</h2>
      <p>We use the following sub-processors to deliver our services, each bound by Data Processing Agreements:</p>
      <ul>
        <li><strong>Google Firebase</strong> — Authentication and database (USA; SCCs in place)</li>
        <li><strong>Stripe Inc.</strong> — Payment processing (USA; SCCs in place; PCI-DSS compliant)</li>
        <li><strong>Render Services</strong> — Cloud hosting (USA; SCCs in place)</li>
        <li><strong>Resend</strong> — Transactional email (USA; SCCs in place)</li>
      </ul>

      <div className="legal-divider" />

      <h2 id="dpo">9. Data Protection Officer</h2>
      <p>For all data protection matters, you can contact our data protection contact point:</p>
      <ul>
        <li>Email: <a href="mailto:support@clicksaver.app">support@clicksaver.app</a></li>
      </ul>

      <div className="legal-divider" />

      <h2 id="complaint">10. Right to Lodge a Complaint</h2>
      <p>If you believe we have not handled your personal data in accordance with the GDPR, you have the right to lodge a complaint with your local supervisory authority. In the EU, you can find your local data protection authority at <a href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" target="_blank" rel="noopener noreferrer">edpb.europa.eu</a>.</p>

      <div className="legal-divider" />

      <h2 id="contact">11. Contact</h2>
      <p>For any GDPR-related requests or questions:</p>
      <ul>
        <li>Email: <a href="mailto:support@clicksaver.app">support@clicksaver.app</a></li>
        <li>Subject line: "GDPR Request — [Your Name]"</li>
        <li>We will acknowledge your request within 72 hours and respond fully within 30 days.</li>
      </ul>

    </LegalPage>
  );
}
